import axios from "axios";
import _ from "lodash";
import ReactGA from "react-ga";
import moment from "moment";
import { normalize } from "normalizr";
import * as R from "ramda";
import camelize from "camelize";

import actionTypes from "./actionTypes";
import fApi from "./firebaseApi";
import { menuSchema, mealSchema, corpMenuSchema } from "./schemas";

const API_URL = "https://vendor-api.asapme.club/";

const apiRequest = (urlSuffix, params = {}, callback = () => {}) =>
  axios
    .post(`${API_URL + urlSuffix}/`, params, { withCredentials: true })
    .then(res => callback(res), error => callback(null, error));

const authLogout = () => {
  apiRequest("auth/logout", {}, (res) => {
    if (res.data.status === "success") {
      window.store.dispatch({
        type: actionTypes.LOGOUT,
      });
    }
  });
};

const authLogin = (username, password, onAuthFailed) =>
  apiRequest(
    "auth/login",
    {
      username,
      password,
    },
    async (res) => {
      console.log("DTDBG: auth/login result", res);
      if (res && res.data.status === "success") {
        ReactGA.event({
          category: "Login",
          action: "login_successful",
        });
        await getVenueInfo();
        window.store.dispatch({
          type: actionTypes.AUTH_SUCCESS,
          auth: true,
          name: res.data.name,
          registerStatus: res.data.register_status,
          registerCompletedSteps: _.map(res.data.completed_steps, c => c === "1"),
        });
        mealItems().then(() => {
          mealList();
        });
        menuList();
        starsItemsList();
        loadTags();
        guestsList();
        // loadReviews();
        loadItemFeedbacksRating();
        fetchVenueFeedbacks();
        fApi.updateVenueName(res.data.name);
        fApi.updateToken();
        fApi.loadChats();
      } else {
        ReactGA.event({
          category: "Login",
          action: "login_failed",
        });
        onAuthFailed(res ? "wrong_auth" : "no_connection");
      }
    },
  );

const mealList = () =>
  apiRequest("meal/list", {}, (res) => {
    if (res && res.data.meals) {
      const lunchMenu = res.data.meals;
      const normalizedMeals = normalize(lunchMenu, [mealSchema]);
      window.store.dispatch({
        type: actionTypes.LOAD_LUNCH_MENU,
        lunchMenu,
        entities: normalizedMeals.entities,
      });
    }
  });

const guestsList = () =>
  apiRequest("clients/list", {}, (res) => {
    const guests = [];
    const guestsById = {};
    if (res.data) {
      res.data.data.clients.forEach((guest) => {
        const guestObj = {
          ...guest,
          sum_cost: Number(guest.sum_cost),
          average_bill: Math.round(guest.sum_cost / guest.count_orders),
        };
        guests.push(guestObj);
        guestsById[guest.id] = guestObj;
      });
    }
    window.store.dispatch({
      type: actionTypes.LOAD_CLIENTS,
      guests,
      guestsById,
    });
  });

const mealCategoryDelete = categoryId =>
  apiRequest(
    "meal/category/delete",
    {
      category_id: categoryId,
    },
    () => mealList(),
  );

const mealCategoryCreate = (menuId, name, items) =>
  apiRequest(
    "meal/category/create",
    {
      menu_id: menuId,
      name,
      items,
    },
    () => mealList(),
  );

const mealCategoryEdit = (lunchId, categoryId, name, items) =>
  apiRequest(
    "meal/category/edit",
    {
      category_id: categoryId,
      name,
      items,
    },
    (res) => {
      if (res && res.data.status === "success") {
        // const item = _.find(window.store.getState().lunch.items, ["id", Number(lunchId)]);
        // item.offers.forEach((offer) => {
        //   if (offer.categories.indexOf(categoryId) !== -1) {
        //     mealOfferEdit(offer.id, offer.name, offer.cost, offer.days, offer.categories);
        //   }
        // });
        return Promise.all([mealList(), mealItems()]);
      }
      return null;
    },
  );

const mealDelete = (menuId) => {
  const params = {
    menu_id: menuId,
  };
  return apiRequest("meal/delete", params, () => mealList());
};

const updateLunchOffers = offers => apiRequest("meal/update", { offers }, () => mealList());

const mealEdit = (menuId, name, days, periods, isOffice, active = 1) => {
  const params = {
    menu_id: menuId,
    name,
    permanent: 0,
    days,
    periods: days.split("").map((day, index) => {
      const period = {};
      period.active = day;
      period.from = periods.time_from[index];
      period.to = periods.time_to[index];
      return period;
    }),
    active,
    is_office_menu: isOffice,
  };
  return apiRequest("meal/edit", params, () => mealList());
};

const mealCreate = (name, days, periods, isOffice) => {
  const params = {
    name,
    permanent: 0,
    days,
    periods: days.split("").map((day, index) => {
      const period = {};
      period.active = day;
      period.from = periods.time_from[index];
      period.to = periods.time_to[index];
      return period;
    }),
    active: 1,
    is_office_menu: isOffice,
  };
  return apiRequest("meal/create", params, res => mealList().then(() => res.data.meal_id));
};

const mealDuplicate = menuId => apiRequest("meal/duplicate", { menu_id: menuId }, () => mealList());

const mealOfferEdit = (offerId, name, cost, days, categories) => {
  console.log("DTDBG: categories", categories);
  const params = {
    offer_id: offerId,
    name,
    cost,
    days,
    categories,
    active: 1,
  };
  return apiRequest("meal/offer/edit", params).then(mealList);
};

const mealOfferCreate = (menuId, name, cost, days, categories) => {
  const params = {
    menu_id: menuId,
    name,
    cost,
    days,
    categories,
    active: 1,
  };
  return apiRequest("meal/offer/create", params, () => mealList());
};

const mealOfferDelete = (offerId) => {
  const params = {
    offer_id: offerId,
  };
  return apiRequest("meal/offer/delete", params, () => mealList());
};

const mealItems = () =>
  apiRequest("meal/items", {}, (res, err) => {
    console.log("DTDBG: res", res, err);
    loadVenueMenu(res.data);
  });

const loadVenueMenu = (venue) => {
  const menu = [];
  const menuById = {};
  console.log("DTDBG: venue", venue);
  venue.menu.forEach((venueMenu) => {
    if (venueMenu.type === "common") {
      venueMenu.categories.forEach((category) => {
        category.positions.forEach((position) => {
          menu.push({
            ...position,
            menuName: venueMenu.name,
            categoryName: category.name,
          });
          menuById[position.id] = {
            ...position,
            menuName: venueMenu.name,
            categoryName: category.name,
          };
        });
      });
    }
  });
  console.log("DTDBG: menu", menu);
  window.store.dispatch({
    type: actionTypes.LOAD_VENUE_MENU,
    positions: venue.positions,
    menuById,
    menu,
  });
};

const loadItemFeedbacksRating = () =>
  apiRequest("vendor/venue/item-feedbacks-rating", {}, (res) => {
    window.store.dispatch({
      type: actionTypes.LOAD_ITEM_FEEDBACKS_RATING,
      rating: res.data.rating,
    });
  });

const updateWelcome = (welcome) => {
  const params = {
    welcome: {
      try_position_ids: welcome,
    },
  };
  return apiRequest("settings/welcome/edit", params, () => {
    window.store.dispatch({
      type: actionTypes.UPDATE_WELCOME,
      welcome,
    });
  });
};

const setCurrency = value =>
  apiRequest(
    "settings/set-currency",
    { value },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.SET_CURRENCY,
        value,
      }),
  );

const loadReviews = () =>
  apiRequest(
    "clients/reviews",
    {
      venue_id: window.store.getState().settings.venue_id,
    },
    (res) => {
      console.log("DTDBG: reviews", res);
      const reviews = res.data.feedbacks.reverse();
      const { garcons } = res.data;
      if (reviews) {
        window.store.dispatch({
          type: actionTypes.LOAD_REVIEWS,
          reviews,
          garcons,
        });
      }
    },
  );

const venueAdmin = (userId) => {
  apiRequest("vendor/venue/admin-update", { user_id: userId }, (res) => {
    console.log("DTDBG: venue/admin res", res);
    window.store.dispatch({
      type: actionTypes.SET_NEW_ADMIN,
      admin: res.data.admin,
    });
  });
};

const venueAdminRemove = () => {
  apiRequest("vendor/venue/admin-remove", {}, () => venueAdmin());
};

const getSupportAgentsInfo = (supportAgents) => {
  apiRequest("vendor/venue/support-info", { support_agents: supportAgents }, (res, error) => {
    console.log("DTDBG: support-info res", res, error);
    window.store.dispatch({
      type: actionTypes.LOAD_SUPPORT,
      supportAgents: _.keyBy(res.data.support_agents, "id"),
    });
  });
};

const guestOrders = (userId, callback) =>
  apiRequest("clients/orders", { user_id: userId }, (res) => {
    console.log("DTDBG: clients/orders", res.data);
    callback(res.data);
  });

const setNewSchedule = newSchedule =>
  apiRequest(
    "vendor/venue/schedule-update",
    {
      schedule: newSchedule,
    },
    () => {
      window.store.dispatch({
        type: actionTypes.UPDATE_SCHEDULE,
        schedule: newSchedule,
      });
    },
  );

const updatePhone = phone =>
  apiRequest("settings/update-phone", { phone }, res =>
    console.log("ASDBG: vendor/venue/update-phone", res.data));

const orderList = (dateFrom, dateTo, onlyCorp = false) => {
  const utcDateFrom = moment(dateFrom);
  const utcDateTo = moment(dateTo);

  const translateStatus = (status) => {
    switch (status) {
      case "closed":
        return "выдан";
      case "canceled":
        return "отменен";
      case "rejected":
        return "отменен";
      case "refused":
        return "отменен";
      default:
        return "готовится";
    }
  };

  return apiRequest(
    "orders/list",
    { from: utcDateFrom.format(), to: utcDateTo.format(), only_corp: onlyCorp },
    (res) => {
      console.log("DTDBG: orders/list response", res.data.orders);
      const orders = res.data.orders.map(order => ({
        ...order,
        date: moment(order.date),
        // FIXME: add timezones
        // .utc(order.date)
        // .subtract(order.date.timezone_type, "hours")
        // .local(),
        status: translateStatus(order.status),
      }));

      window.store.dispatch({
        type: actionTypes.UPDATE_ORDER_LIST,
        orders,
      });
    },
  );
};

const orderChangeType = (orderId, newType) =>
  apiRequest("orders/change-type", { id: orderId, new_type: newType }, (res, err) => {
    console.log("ASDBG orders/change-type", res, err);
  });

const loadWeeklyDashboard = (period = "week") =>
  apiRequest("dashboard/stats", { period }, (res) => {
    console.log("AZDBG: dashboard/stats", res.data);
    const stats = res.data;
    window.store.dispatch({
      type: actionTypes.LOAD_DASHBOARD_STATS,
      stats,
    });
  });

const loadEventsFeed = limit =>
  apiRequest("dashboard/events", { limit }, (res) => {
    console.log("AZDBG: dashboard/events", res.data);
    window.store.dispatch({
      type: actionTypes.LOAD_DASHBOARD_EVENTS,
      events: _.orderBy(res.data.events, "event_time", "desc").slice(0, limit),
    });
  });

const menuList = () =>
  apiRequest("menu/list", {}, (res) => {
    const normalizedMenu = normalize(res.data.menu, [menuSchema]);
    const fullMenu = _.groupBy(res.data.menu, "supercategory");
    console.log("ASDBG: menu/list res", res.data.menu, fullMenu);
    window.store.dispatch({
      type: actionTypes.LOAD_FULL_MENU,
      fullMenu,
      entities: normalizedMenu.entities,
    });
  });

const menuItemEdit = (
  positionId,
  itemId,
  name,
  cost,
  description,
  duration,
  portion,
  fat,
  carbohydrates,
  protein,
  kcal,
  volume,
  toppings,
) => {
  console.log("ASDBG 2", volume, toppings);
  return apiRequest(
    "menu/item-edit",
    {
      item_id: itemId,
      position_id: positionId,
      name,
      cost,
      description,

      duration,

      portion,
      fat,
      carbohydrates,
      protein,
      kcal,
      volume,
      toppings,
    },
    (res, err) => {
      console.log("ASDBG: menu/item-edit", res, err);
    },
  );
};

const menuItemDelete = positionId =>
  apiRequest("menu/item-delete", { pos_id: positionId }, (res, err) => {
    console.log("ASDBG menu/item-delete", res, err);
  });

const menuItemSetActive = (positionId, isActive) =>
  apiRequest(
    "menu/set-item-active",
    {
      position_id: positionId,
      is_active: isActive === 1 ? "on" : "off",
    },
    (res, err) => {
      console.log("ASDBG menu/set-item-active", res, err);
    },
  );

const menuItemSetDelivery = (positionId, delivery) =>
  apiRequest(
    "menu/set-item-delivery",
    {
      position_id: positionId,
      delivery,
    },
    (res, err) => {
      console.log("ASDBG menu/set-item-delivery", res, err);
    },
  );

const menuReorderPositions = positionIds =>
  apiRequest("menu/reorder-positions", { position_ids: positionIds }, (res, err) => {
    console.log("ASDBG menu/reorder-positions", res, err);
  });

const menuUpdateMenuName = (menuId, name) =>
  apiRequest("menu/update-menu-name", { menu_id: menuId, name }, (res, err) => {
    console.log("ASDBG menu/update-menu-name", res, err);
  });

const menuUpdateMenuDescription = (menuId, description) =>
  apiRequest("menu/update-description", { menu_id: menuId, description }, (res, err) => {
    console.log("ASDBG menu/update-description", res, err);
  });

const menuUpdateMenuOfficeAvailability = (menuId, isAvailable) =>
  apiRequest("menu/update-office-availability", { menu_id: menuId, is_office_menu: isAvailable });

const menuUpdateCategoryDelivery = (categoryId, delivery) =>
  apiRequest("menu/update-category-delivery", { category_id: categoryId, delivery }, (res, err) => {
    console.log("ASDBG menu/update-category-delivery", res, err);
  });

const menuUpdateCategoryName = (categoryId, name) =>
  apiRequest("menu/update-category-name", { category_id: categoryId, name }, (res, err) => {
    console.log("ASDBG menu/update-category-name", res, err);
  });

const menuUpdateCategoryDescription = (categoryId, description) =>
  apiRequest("menu/update-category-description", { category_id: categoryId, description });

const menuItemCreate = categoryId =>
  apiRequest("menu/item-create", { category_id: categoryId }, (res, err) => {
    if (R.path(["data", "status"], res) !== "success") {
      throw Error(err);
    }
    return {
      positionId: R.path(["data", "menu_position_id"], res),
    };
  });

const menuCategoryCreate = (menuId, categoryName) =>
  apiRequest("menu/category-create", { menu_id: menuId, name: categoryName }, (res, err) => {
    console.log("ASDBG: menu/category-create", res, err);
  });

const menuCategoryDelete = categoryId =>
  apiRequest("menu/category-delete", { category_id: categoryId }, (res, err) => {
    console.log("ASDBG menu/category-delete", res, err);
  });

const menuCreate = (menuName, supercategory) =>
  apiRequest("menu/create", { menu_name: menuName, supercategory }, (res, err) => {
    console.log("ASDBG: menu/create", res, err);
  });

const menuDelete = menuId =>
  apiRequest("menu/delete", { menu_id: menuId }, (res, err) => {
    console.log("ASDBG menu/delete", res, err);
  });

const menuReorder = menuIds =>
  apiRequest("menu/reorder", { menu_ids: menuIds }, (res, err) => {
    console.log("ASDBG menu/reorder", res, err);
  });

const menuReorderCategories = categoryIds =>
  apiRequest("menu/reorder-categories", { category_ids: categoryIds }, (res, err) => {
    console.log("ASDBG menu/reorder-categories", res, err);
  });

const menuItemCategoryChange = (positionId, newCategoryId) =>
  apiRequest(
    "menu/item-change-category",
    {
      pos_id: positionId,
      new_category_id: newCategoryId,
    },
    (res, err) => {
      console.log("ASDBG menu/item-change-category", res, err);
    },
  );

const menuItemUploadImage = (file, itemId) => {
  const url = `${API_URL}menu/item-upload-image?item_id=${itemId}`;
  const formData = new FormData();
  formData.append("image", file);
  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
    withCredentials: true,
  };
  return axios.post(url, formData, config);
};

const positionStats = (positionId, callback) =>
  apiRequest("position/stats", { position_id: positionId }, callback);

const menuImport = (newPositions, changedPositions, newSorting, deletedPositions) =>
  apiRequest(
    "menu/import",
    {
      new_positions: newPositions,
      changed_positions: changedPositions,
      new_sorting: newSorting,
      deleted_positions: deletedPositions,
    },
    (res, err) => {
      console.log("ASDBG menu/import", res, err);
    },
  );

const loadCorpMenu = () =>
  apiRequest("corp-menu/get", {}, ({ data }) => {
    if (data.status === "success") {
      const { result, entities } = normalize(data.menus, [corpMenuSchema]);
      window.store.dispatch({ type: actionTypes.LOAD_CORP_MENUS, result, entities });
    }
  });

const addCorpMeal = (corpMenuId, id) =>
  apiRequest(
    "corp-menu/add-meal",
    { corp_menu_id: corpMenuId, id },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_CORP_MENU,
        corpMenuId,
        corpMenu: res.data.menu,
      }),
  );

const deleteCorpMeal = (corpMenuId, id) =>
  apiRequest(
    "corp-menu/delete-meal",
    { corp_menu_id: corpMenuId, id },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_CORP_MENU,
        corpMenuId,
        corpMenu: res.data.menu,
      }),
  );

const addComposition = (corpMenuId, name) =>
  apiRequest(
    "corp-menu/add-composition",
    { corp_menu_id: corpMenuId, name },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_CORP_MENU,
        corpMenuId,
        corpMenu: res.data.menu,
      }),
  );

const addCompositionCategory = (corpMenuId, id, name) =>
  apiRequest(
    "corp-menu/add-category",
    { corp_menu_id: corpMenuId, id, name },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_CORP_MENU,
        corpMenuId,
        corpMenu: res.data.menu,
      }),
  );

const addCompositionPosition = (corpMenuId, categoryId, id) =>
  apiRequest(
    "corp-menu/add-position",
    { corp_menu_id: corpMenuId, id, category_id: categoryId },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_CORP_MENU,
        corpMenuId,
        corpMenu: res.data.menu,
      }),
  );

const deleteCompositionPosition = (corpMenuId, categoryId, id) =>
  apiRequest(
    "corp-menu/delete-position",
    { corp_menu_id: corpMenuId, id, category_id: categoryId },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_CORP_MENU,
        corpMenuId,
        corpMenu: res.data.menu,
      }),
  );

const deleteCompositionCategory = (corpMenuId, id) =>
  apiRequest(
    "corp-menu/delete-category",
    { corp_menu_id: corpMenuId, id },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_CORP_MENU,
        corpMenuId,
        corpMenu: res.data.menu,
      }),
  );

const deleteComposition = (corpMenuId, id) =>
  apiRequest(
    "corp-menu/delete-composition",
    { corp_menu_id: corpMenuId, id },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_CORP_MENU,
        corpMenuId,
        corpMenu: res.data.menu,
      }),
  );

const setCorpActive = (corpMenuId, value) =>
  apiRequest(
    "corp-menu/set-active",
    { corp_menu_id: corpMenuId, value },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_CORP_MENU,
        corpMenuId,
        corpMenu: res.data.menu,
      }),
  );

const loadTags = () =>
  apiRequest(
    "menu/tags",
    {},
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_TAGS,
        tags: _.keyBy(res.data.tags, "id"),
        tagCategories: _.keyBy(res.data.tag_categories, "id"),
      }),
  );

const loadGifts = () =>
  apiRequest(
    "gifts/get",
    {},
    res =>
      res.data.status === "success" &&
      window.store.dispatch({ type: actionTypes.GIFTS_LOADED, gifts: res.data.gifts }),
  );

const setGiftsActive = value =>
  apiRequest(
    "gifts/set-active",
    { value },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({ type: actionTypes.GIFTS_LOADED, gifts: res.data.gifts }),
  );

const setGiftsPromocode = value =>
  apiRequest(
    "gifts/change-promocode",
    { value },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({ type: actionTypes.GIFTS_LOADED, gifts: res.data.gifts }),
  );

const addGiftsPosition = id =>
  apiRequest(
    "gifts/add",
    { id },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({ type: actionTypes.GIFTS_LOADED, gifts: res.data.gifts }),
  );

const deleteGiftsPosition = id =>
  apiRequest(
    "gifts/delete",
    { id },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({ type: actionTypes.GIFTS_LOADED, gifts: res.data.gifts }),
  );

const createTags = tags => apiRequest("menu/tags-create", { tags });

const setPackingCost = newCost =>
  apiRequest(
    "meal/packing-cost",
    { packing_cost: newCost },
    ({ data: { status, packing_cost: packingCost } }) =>
      status === "success" &&
      window.store.dispatch({
        type: actionTypes.SET_PACKING_COST,
        packing_cost: Number(packingCost),
      }),
  );

const setDeliverySettings = newSettings =>
  apiRequest(
    "vendor/venue/delivery-settings",
    newSettings,
    ({ data: { status, delivery } }) =>
      status === "success" &&
      window.store.dispatch({
        type: actionTypes.SET_DELIVERY_SETTINGS,
        delivery,
      }),
  );

const starsItemsList = () =>
  apiRequest("stars/items-list", {}, (res) => {
    console.log("ASDBG: stars/items-list res", res.data.stars_items);
    window.store.dispatch({
      type: actionTypes.LOAD_STARS_ITEMS,
      starsItems: res.data.stars_items,
      starsActive: res.data.stars_active,
      promocode: res.data.promocode,
      promocodeAmount: res.data.promocode_amount,
    });
  });

const setStarsPromocode = (code, amount) =>
  apiRequest(
    "stars/change-promocode",
    { code, amount },
    res =>
      res.data.status === "success" &&
      window.store.dispatch({
        type: actionTypes.LOAD_STARS_PROMOCODE,
        promocode: res.data.promocode,
        promocodeAmount: res.data.promocode_amount,
      }),
  );

const updateStarsItemName = (id, name) =>
  apiRequest(
    "stars/item-update-name",
    {
      id,
      name,
    },
    (res) => {
      console.log("ASDBG: stars/items-update-name res", res.data);
    },
  );

const updateStarsItemDescription = (id, description) =>
  apiRequest(
    "stars/item-update-description",
    {
      id,
      description,
    },
    (res) => {
      console.log("ASDBG: stars/items-update-description res", res.data);
    },
  );

const updateStarsItemCost = (id, cost) =>
  apiRequest(
    "stars/item-update-cost",
    {
      id,
      cost,
    },
    (res) => {
      console.log("ASDBG: stars/items-update-cost res", res.data);
    },
  );

const updateStarsItemGroupCost = (ids, cost) =>
  apiRequest(
    "stars/item-group-update-cost",
    {
      item_ids: ids,
      cost,
    },
    (res) => {
      console.log("ASDBG: stars/item-group-update-cost res", res.data);
    },
  );

const createStarsImpression = (name, description, cost, isGift) =>
  apiRequest(
    "stars/create-impression",
    {
      name,
      description,
      cost,
      is_gift: isGift,
    },
    (res) => {
      console.log("ASDBG: stars/items-update-cost res", res.data);
    },
  );

const createStarsMenuPosition = (positionId, cost, isGift) =>
  apiRequest(
    "stars/create-menu-position",
    {
      position_id: positionId,
      cost,
      is_gift: isGift,
    },
    (res) => {
      console.log("ASDBG: stars/create-menu-position res", res.data);
    },
  );

const deleteStarsItem = starsItemId =>
  apiRequest(
    "stars/delete-item",
    {
      stars_item_id: starsItemId,
    },
    (res) => {
      console.log("ASDBG: stars/delete-item res", res.data);
    },
  );

const setStarsActive = isActive =>
  apiRequest(
    "stars/set-active",
    {
      is_active: isActive,
    },
    (res) => {
      console.log("ASDBG: stars/set-active res", res.data);
    },
  );

const uploadBanner = (imageData) => {
  const url = `${API_URL}vendor/venue/upload-banner`;
  console.log("ASDBG", url);
  const formData = new FormData();
  formData.append("image", imageData);
  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
    withCredentials: true,
  };
  return axios.post(url, formData, config);
};

const uploadIcon = (imageData) => {
  const url = `${API_URL}vendor/venue/upload-icon`;
  console.log("ASDBG", url);
  const formData = new FormData();
  formData.append("image", imageData);
  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
    withCredentials: true,
  };
  return axios.post(url, formData, config);
};

const addVenueTags = tags =>
  apiRequest("settings/add-tags", { tags }, (res) => {
    console.log("ASDBG: settings/add-tags res", res.data);
  });

const updateVenueDescription = description =>
  apiRequest("settings/update-description", { description }, (res) => {
    console.log("ASDBG: settings/update-description res", res.data);
  });

const updateVenueMenuDescription = menuDescription =>
  apiRequest("settings/update-menu-description", { menu_description: menuDescription });

const updateVenueAddress = address =>
  apiRequest("settings/update-address", { address }, (res) => {
    console.log("ASDBG: settings/update-address res", res.data);
  });

const uploadGalleryImage = (imageData) => {
  const url = `${API_URL}vendor/venue/upload-gallery-image`;
  console.log("ASDBG", url);
  const formData = new FormData();
  formData.append("image", imageData);
  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
    withCredentials: true,
  };
  return axios.post(url, formData, config);
};

const updateVenuePaymentSettings = (
  legalName,
  taxId,
  KPP,
  checkingAccount,
  correspondentAccount,
  bankName,
  BIK,
  ceoName,
) =>
  apiRequest(
    "settings/update-payment-settings",
    {
      legal_name: legalName,
      tax_id: taxId,
      kpp: KPP,
      checking_account: checkingAccount,
      correspondent_account: correspondentAccount,
      bank_name: bankName,
      bik: BIK,
      ceo_name: ceoName,
    },
    (res) => {
      console.log("ASDBG: settings/update-payment-settings res", res.data);
    },
  );

const updateTariff = tariff =>
  apiRequest("settings/update-tariff", { tariff }, (res) => {
    console.log("ASDBG: settings/update-tariff", res.data);
    if (res.data.payment_settings) {
      window.store.dispatch({
        type: actionTypes.UPDATE_PAYMENT_SETTINGS,
        paymentSettings: res.data.payment_settings,
      });
    }
  });

const proceedRegister = (step, completedStepsStr) =>
  apiRequest("vendor/venue/proceed-register", { step, completed_steps: completedStepsStr }, (res) => {
    window.store.dispatch({
      type: actionTypes.UPDATE_REGISTER_STEPS,
      registerCompletedSteps: _.map(res.data.completed_steps, c => c === "1"),
    });
    console.log("ASDBG: vendor/venue/proceed-register", res.data);
  });

const getVenueInfo = () =>
  apiRequest("vendor/venue/info", {}, (res) => {
    console.log("ASDBG: vendor/venue/info", res.data);
    window.store.dispatch({
      type: actionTypes.LOAD_VENUE_INFO,
      settings: res.data.settings,
      schedule: res.data.schedule,
      admin: res.data.admin,
      address: res.data.address,
      welcome: (JSON.parse(res.data.settings.welcome) || {}).try_position_ids || [],
      paymentSettings: res.data.payment_settings,
      venueTags: _.map(res.data.venue_tags, "content"),
      banner: res.data.banner_image,
      icon: res.data.icon_image,
      gallery: res.data.gallery_images,
      commission: res.data.commission,
    });
  });

const generateStaffPassword = callback =>
  apiRequest("vendor/venue/generate-staff-password", {}, callback);

const starsImpressionUploadItems = (file, impressionId) => {
  const url = `${API_URL}stars/impression-upload-image?impression_id=${impressionId}`;
  const formData = new FormData();
  formData.append("image", file);
  const config = {
    headers: {
      "content-type": "multipart/form-data",
    },
    withCredentials: true,
  };
  return axios.post(url, formData, config);
};

const importStarsItems = (newImpressions, updatedImpressions, newMenuPositions, itemsToDelete) =>
  apiRequest(
    "stars/import",
    {
      new_impressions: newImpressions,
      updated_impressions: updatedImpressions,
      new_menu_positions: newMenuPositions,
      items_to_delete: itemsToDelete,
    },
    (res) => {
      console.log("ASDBG: stars/import res", res.data.stars_items);
      window.store.dispatch({
        type: actionTypes.LOAD_STARS_ITEMS,
        starsItems: res.data.stars_items,
        starsActive: res.data.stars_active,
      });
    },
  );

const updateStarsImpression = (impressionId, name, description) =>
  apiRequest(
    "stars/update-impression",
    {
      impression_id: impressionId,
      name,
      description,
    },
    (res) => {
      console.log("ASDBG: stars/update-impression res", res.data);
    },
  );

const createSettingsMail = (mail) => {
  apiRequest(
    "settings/create-mail",
    {
      mail,
    },
    () => {
      getVenueInfo();
    },
  );
};

const updateSettingsMail = (mail, id = null) => {
  apiRequest(
    "settings/update-mail",
    {
      mail,
      id,
    },
    () => {
      getVenueInfo();
    },
  );
};

const deleteSettingsMail = (id) => {
  apiRequest(
    "settings/delete-mail",
    {
      id,
    },
    () => {
      getVenueInfo();
    },
  );
};

const fetchVenueFeedbacks = (limit = 50) =>
  apiRequest("feedbacks/venue-list", { limit }, (res) => {
    window.store.dispatch({
      type: actionTypes.LOAD_VENUE_FEEDBACKS,
      feedbacks: res.data.feedbacks,
    });
  });

const updateCommission = commission =>
  apiRequest("vendor/venue/update-commission", {
    commission: _.mapKeys(commission, (__, key) => _.snakeCase(key)),
  });

const updateVenueScannerSettings = ({ isScannerOnly, hasScanner }) =>
  apiRequest("settings/update-venue-scanner-settings", {
    is_scanner_only: isScannerOnly,
    has_scanner: hasScanner,
  });

const addTryOutPosition = positionId =>
  apiRequest("menu/add-try-out-position", { position_id: positionId });

const removeTryOutPosition = positionId =>
  apiRequest("menu/remove-try-out-position", { position_id: positionId });

const fetchLoyaltyHistory = ({ date }) =>
  apiRequest("stars/history", { date }, (res) => {
    if (R.path(["data", "status"], res) === "success") {
      return res.data.stars_incomes;
    }
    return [];
  });

const fetchLoyaltyStats = ({ date }) =>
  apiRequest("stars/stats", { date }, (res) => {
    if (R.path(["data", "status"], res) === "success") {
      return res.data.stars_stats;
    }
    return [];
  });

const fetchLoyaltyNetworkStats = ({ date }) =>
  apiRequest("stars/network-stats", { date }, (res) => {
    if (R.path(["data", "status"], res) === "success") {
      return res.data.stars_stats;
    }
    return [];
  });

const emailWeeklyStats = () => apiRequest("orders/email-weekly-stats", {});

const getVenueAdmins = () =>
  apiRequest("vendor/venue/get-admins", {}, (res) => {
    if (R.path(["data", "status"], res) === "success") {
      return camelize(res.data.admins);
    }
    return [];
  });

const addVenueAdmin = userId => apiRequest("vendor/venue/add-admin", { user_id: userId });

const deleteVenueAdmin = userId => apiRequest("vendor/venue/delete-admin", { user_id: userId });

const setVenueAdminStarsNotification = (userId, isActive) =>
  apiRequest("vendor/venue/set-admin-stars-notification", { user_id: userId, is_active: isActive });

const setVenueAdminStatsNotification = (userId, isActive) =>
  apiRequest("vendor/venue/set-admin-stats-notification", { user_id: userId, is_active: isActive });

export default {
  updateLunchOffers,
  loadEventsFeed,
  loadWeeklyDashboard,
  loadCorpMenu,
  loadReviews,
  loadItemFeedbacksRating,
  mealItems,
  mealCreate,
  mealEdit,
  mealDuplicate,
  mealDelete,
  mealList,
  mealOfferCreate,
  mealOfferDelete,
  mealOfferEdit,
  mealCategoryCreate,
  mealCategoryEdit,
  mealCategoryDelete,
  authLogin,
  authLogout,
  guestsList,
  guestOrders,
  updateWelcome,
  venueAdmin,
  venueAdminRemove,
  getSupportAgentsInfo,
  setNewSchedule,
  updatePhone,
  orderList,
  orderChangeType,
  menuList,
  menuItemEdit,
  menuItemSetActive,
  menuItemSetDelivery,
  menuReorderPositions,
  menuUpdateMenuName,
  menuUpdateMenuDescription,
  menuUpdateMenuOfficeAvailability,
  menuUpdateCategoryDelivery,
  menuUpdateCategoryName,
  menuUpdateCategoryDescription,
  menuItemCreate,
  menuReorder,
  menuDelete,
  menuReorderCategories,
  menuCategoryCreate,
  menuCategoryDelete,
  menuCreate,
  menuItemDelete,
  menuItemCategoryChange,
  menuItemUploadImage,
  positionStats,
  menuImport,
  addCorpMeal,
  deleteCorpMeal,
  addComposition,
  addCompositionCategory,
  addCompositionPosition,
  deleteCompositionPosition,
  deleteCompositionCategory,
  deleteComposition,
  setCorpActive,
  loadTags,
  createTags,
  loadGifts,
  setGiftsActive,
  setGiftsPromocode,
  addGiftsPosition,
  deleteGiftsPosition,
  setPackingCost,
  setDeliverySettings,
  setCurrency,
  starsItemsList,
  updateStarsItemName,
  updateStarsItemDescription,
  updateStarsItemCost,
  updateStarsItemGroupCost,
  createStarsImpression,
  createStarsMenuPosition,
  deleteStarsItem,
  setStarsActive,
  uploadBanner,
  uploadIcon,
  addVenueTags,
  updateVenueScannerSettings,
  updateVenueDescription,
  updateVenueMenuDescription,
  updateVenueAddress,
  updateVenuePaymentSettings,
  updateTariff,
  proceedRegister,
  uploadGalleryImage,
  generateStaffPassword,
  getVenueInfo,
  setStarsPromocode,
  starsImpressionUploadItems,
  importStarsItems,
  updateStarsImpression,
  createSettingsMail,
  updateSettingsMail,
  deleteSettingsMail,
  fetchVenueFeedbacks,
  updateCommission,
  addTryOutPosition,
  removeTryOutPosition,
  fetchLoyaltyHistory,
  fetchLoyaltyStats,
  fetchLoyaltyNetworkStats,
  emailWeeklyStats,
  getVenueAdmins,
  addVenueAdmin,
  deleteVenueAdmin,
  setVenueAdminStarsNotification,
  setVenueAdminStatsNotification,
};



// WEBPACK FOOTER //
// ./src/api.js